import { MoreHorizontal } from "lucide-react";
import type { ProfilePage } from "@amber/application/contracts";
import { EDITOR_IDENTITY_REASON } from "@amber/profiles/contracts";
import type { LifecycleKind } from "@amber/profiles/ui";
import { Button, MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from "@amber/ui";
import type { ProfilePageState, ProfilePanel } from "./pageState";

function MoreActions({
  p,
  state,
  onPanel,
  onLifecycle,
}: {
  p: ProfilePage;
  state: ProfilePageState;
  onPanel: (panel: ProfilePanel) => void;
  onLifecycle: (kind: LifecycleKind) => void;
}) {
  const { editReason, deleteReason } = state;
  return (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button size="sm" aria-label="More actions">
          <MoreHorizontal size={15} aria-hidden="true" />
        </Button>
      </MenuTrigger>
      <MenuContent>
        <MenuItem
          disabled={editReason !== null}
          title={editReason ?? undefined}
          onSelect={() => {
            onPanel("identity");
          }}
        >
          Edit identity…
        </MenuItem>
        <MenuItem
          disabled={editReason !== null || !p.identity.wikidata_id}
          title={editReason ?? (p.identity.wikidata_id ? undefined : "No Wikipedia identity to refresh from")}
          onSelect={() => {
            onPanel("refresh");
          }}
        >
          Refresh from Wikipedia…
        </MenuItem>
        <MenuItem
          disabled={editReason !== null}
          title={editReason ?? undefined}
          onSelect={() => {
            onPanel("merge");
          }}
        >
          Merge with another profile…
        </MenuItem>
        <MenuItem
          disabled={editReason !== null}
          title={editReason ?? undefined}
          onSelect={() => {
            onPanel("split");
          }}
        >
          Split into a new profile…
        </MenuItem>
        <MenuSeparator />
        {p.lifecycle_state === "draft" ? (
          <MenuItem
            disabled={deleteReason !== null}
            title={deleteReason ?? undefined}
            onSelect={() => {
              onLifecycle("delete");
            }}
          >
            Delete draft…
          </MenuItem>
        ) : (
          <MenuItem
            disabled={editReason !== null}
            title={editReason ?? undefined}
            onSelect={() => {
              onLifecycle("retire");
            }}
          >
            Retire profile…
          </MenuItem>
        )}
      </MenuContent>
    </MenuRoot>
  );
}

export function ProfileActions({
  p,
  state,
  editor,
  onPanel,
  onLifecycle,
}: {
  p: ProfilePage;
  state: ProfilePageState;
  editor: boolean;
  onPanel: (panel: ProfilePanel) => void;
  onLifecycle: (kind: LifecycleKind) => void;
}) {
  return (
    <span className="ml-auto flex gap-2">
      {state.draftLike ? (
        <Button
          variant="primary"
          size="sm"
          disabledReason={state.startReason}
          onClick={() => {
            onPanel("start");
          }}
        >
          Start tracking…
        </Button>
      ) : null}
      {state.retired ? (
        <Button
          variant="primary"
          size="sm"
          disabledReason={editor ? null : EDITOR_IDENTITY_REASON}
          onClick={() => {
            onLifecycle("reactivate");
          }}
        >
          Reactivate…
        </Button>
      ) : null}
      {p.lifecycle_state === "active" || p.lifecycle_state === "paused" ? (
        <>
          <Button
            size="sm"
            disabledReason={editor ? null : "Only Editors and above can change coverage plans"}
            onClick={() => {
              onPanel("plan");
            }}
          >
            Edit plan
          </Button>
          <Button
            size="sm"
            disabledReason={editor ? null : "Only Editors and above can pause or resume a person"}
            onClick={() => {
              onPanel("pause");
            }}
          >
            {p.lifecycle_state === "paused" ? "Resume…" : "Pause…"}
          </Button>
        </>
      ) : null}
      <MoreActions p={p} state={state} onPanel={onPanel} onLifecycle={onLifecycle} />
    </span>
  );
}
